import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import Calendar from "@/components/ui/calendar";
import IconContainer from "@/components/ui/icon-container";
import {
  ChevronDown,
  ChevronUp,
  EmptyStateIllustrationOnboardingNotStarted
} from "@dreamhub/designsystem/icons";
import { simpleDate } from "@/utils/datetime";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { Calendar as CalendarIcon, Play } from "lucide-react";

interface Props {
  onboardingStartDate: string | null;
  isDatePickerOpen: boolean;
  setIsDatePickerOpen: (open: boolean) => void;
  onSelectStartDate: (date: Date) => void;
  onStartNow: () => void;
}

const OnboardingNotStartedEmptyState: React.FC<Props> = ({
  onboardingStartDate,
  isDatePickerOpen,
  setIsDatePickerOpen,
  onSelectStartDate,
  onStartNow
}) => {
  const { t } = useTranslation();
  const [isScheduledMenuOpen, setIsScheduledMenuOpen] = useState(false);

  const selectedDate = onboardingStartDate
    ? new Date(onboardingStartDate)
    : null;

  const renderCalendarPopoverContent = () => (
    <PopoverContent
      padding={4}
      className="w-fit pt-2"
      align="start"
      sideOffset={4}
      avoidCollisions
    >
      <Calendar
        mode="single"
        selected={selectedDate ?? undefined}
        defaultMonth={selectedDate ?? new Date()}
        onSelect={(date) => {
          if (date) onSelectStartDate(date);
        }}
      />
    </PopoverContent>
  );

  return (
    <div
      className="flex items-center justify-center w-full h-full min-h-[600px]"
      data-test="contract-detailed-view-onboarding-not-started"
    >
      <div className="flex flex-col md:flex-row items-center gap-10 max-w-[760px]">
        <div className="flex-shrink-0">
          <EmptyStateIllustrationOnboardingNotStarted />
        </div>
        <div className="flex flex-col gap-6 max-w-[300px]">
          <div className="flex flex-col gap-5">
            <Text variant="lgSubtitleH1">
              {t("contracts.detailedView.onboarding.notStartedYet")}
            </Text>
            <div className="flex flex-col">
              <Text variant="lgBodySecondary">
                {t("contracts.detailedView.onboarding.notStartedDescription")}
              </Text>
              <Text variant="lgBodySecondary">
                {t("contracts.detailedView.onboarding.notStartedDescription2")}
              </Text>
            </div>
          </div>
          {selectedDate ? (
            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
              <PopoverAnchor asChild>
                <div className="w-fit">
                  <OptionsDropdownMenu
                    tPrefix="contracts.detailedView.options"
                    triggerDataTest="contract-detailed-view-onboarding-scheduled-pill"
                    align="start"
                    disableCloseAutoFocus
                    onOpenChange={setIsScheduledMenuOpen}
                    customTrigger={
                      <button type="button" className="w-fit">
                        <Badge className="py-2 px-[10px] cursor-pointer border-border-secondary-button">
                          <div className="flex items-center gap-1">
                            <Text variant="body">
                              {t(
                                "contracts.detailedView.onboarding.startsAt",
                                {
                                  date: simpleDate(selectedDate, "long")
                                }
                              )}
                            </Text>
                            <IconContainer size="5">
                              {isScheduledMenuOpen ? (
                                <ChevronUp />
                              ) : (
                                <ChevronDown />
                              )}
                            </IconContainer>
                          </div>
                        </Badge>
                      </button>
                    }
                    options={{}}
                    topOptions={{
                      startOnboardingNow: {
                        action: onStartNow,
                        customIcon: (
                          <IconContainer size="5" color="secondary">
                            <Play />
                          </IconContainer>
                        )
                      },
                      changeOnboardingStartDate: {
                        action: () =>
                          requestAnimationFrame(() =>
                            setIsDatePickerOpen(true)
                          ),
                        customIcon: (
                          <IconContainer size="5" color="secondary">
                            <CalendarIcon />
                          </IconContainer>
                        )
                      }
                    }}
                  />
                </div>
              </PopoverAnchor>
              {renderCalendarPopoverContent()}
            </Popover>
          ) : (
            <div className="flex gap-2">
              <Popover
                open={isDatePickerOpen}
                onOpenChange={setIsDatePickerOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="secondary"
                    className="h-9 px-[10px]"
                    data-test="contract-detailed-view-onboarding-choose-start-date-button"
                  >
                    {t("contracts.detailedView.onboarding.chooseStartDate")}
                  </Button>
                </PopoverTrigger>
                {renderCalendarPopoverContent()}
              </Popover>
              <Button
                variant="primary"
                className="h-9 px-[10px]"
                onClick={onStartNow}
              >
                {t("contracts.detailedView.onboarding.startNow")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingNotStartedEmptyState;
